import _taggedTemplateLiteral from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/taggedTemplateLiteral.js";
import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";

var _templateObject;

import { AppAlertsContext } from '@infl-ts/ui-app-alerts';
import { useToggle } from '@infl-ts/util-hooks';
import { logout } from '@infl-ts/util-hub-axios-instance';
import { useMutationMapper } from '@infl-ts/util-use-mutation-mapper';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, InputAdornment, TextField, Typography } from '@mui/material';
import { oneLine } from 'common-tags';
import * as E from 'fp-ts/Either';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AsyncButton, SaveStates } from '../../async-button';
import { deleteAccountFailure } from './alerts';
import { useDeleteAccountMutation } from './mutations';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

var DeleteAccountModal = function DeleteAccountModal(_ref) {
  var open = _ref.open,
      setClosed = _ref.setClosed;

  var _useContext = useContext(AppAlertsContext),
      showAlert = _useContext.showAlert;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var _useToggle = useToggle(),
      _useToggle2 = _slicedToArray(_useToggle, 2),
      deleteAccountEnabled = _useToggle2[0],
      _useToggle2$ = _useToggle2[1],
      enableDeleteAccount = _useToggle2$.on,
      disableDeleteAccount = _useToggle2$.off;

  var handleTextInputChange = function handleTextInputChange(newValue) {
    return newValue === 'DELETE' ? enableDeleteAccount() : disableDeleteAccount();
  };

  var handleClose = function handleClose() {
    setClosed();
    disableDeleteAccount();
  };

  var _useDeleteAccountMuta = useDeleteAccountMutation({
    onCompleted: E.match(function () {
      handleClose();
      showAlert(deleteAccountFailure(deleteAccount, t));
    }, function (_ref2) {
      var deleteMyAccount = _ref2.deleteMyAccount;

      if (deleteMyAccount.__typename === 'DeleteMyAccountResult') {
        logout();
      } else {
        handleClose();
        showAlert(deleteAccountFailure(deleteAccount, t));
      }
    })
  }),
      _useDeleteAccountMuta2 = _slicedToArray(_useDeleteAccountMuta, 2),
      deleteAccount = _useDeleteAccountMuta2[0],
      deleteAccountStatus = _useDeleteAccountMuta2[1];

  var Saving = SaveStates.Saving,
      Default = SaveStates.Default,
      Success = SaveStates.Success,
      Error = SaveStates.Error;
  var mutationValue = useMutationMapper(deleteAccountStatus, function (_ref3) {
    var deleteMyAccount = _ref3.deleteMyAccount;
    return deleteMyAccount.__typename === 'DeleteMyAccountError' ? E.left(deleteMyAccount) : E.right(deleteMyAccount);
  }, [Saving, Success, Error, Default]);
  return /*#__PURE__*/_jsxs(Dialog, {
    open: open,
    onClose: handleClose,
    PaperProps: {
      sx: {
        width: 1,
        maxWidth: {
          xs: 1,
          md: '36vw'
        },
        height: 'min-content',
        p: 2,
        display: 'flex',
        justifyContent: 'center'
      }
    },
    "data-element": "Dialog",
    "data-component": "DeleteAccountModal",
    "data-source-file": "delete-account.tsx",
    children: [/*#__PURE__*/_jsx(DialogTitle, {
      sx: {
        fontSize: function fontSize(t) {
          return t.typography.h3.fontSize;
        }
      },
      "data-element": "DialogTitle",
      "data-component": "DeleteAccountModal",
      "data-source-file": "delete-account.tsx",
      children: t('Delete Account')
    }), /*#__PURE__*/_jsxs(DialogContent, {
      "data-element": "DialogContent",
      "data-source-file": "delete-account.tsx",
      children: [/*#__PURE__*/_jsx(DialogContentText, {
        "data-element": "DialogContentText",
        "data-source-file": "delete-account.tsx",
        children: t(oneLine(_templateObject || (_templateObject = _taggedTemplateLiteral(["We're sad to see you go! Are you sure you want to delete your account?\n            All of your user data will be deleted forever and you will no longer be able to\n            access your account. You will be logged out once the deletion is processed."]))))
      }), /*#__PURE__*/_jsx(TextField, {
        label: t('Type {{delete}} to confirm', {
          "delete": 'DELETE'
        }),
        id: "deleteAccountConfirmationField",
        onChange: function onChange(e) {
          return handleTextInputChange(e.target.value);
        },
        sx: {
          width: '30vw',
          mt: 3
        },
        InputProps: {
          startAdornment: /*#__PURE__*/_jsx(InputAdornment, {
            position: "start"
          })
        },
        "data-element": "TextField",
        "data-source-file": "delete-account.tsx"
      })]
    }), /*#__PURE__*/_jsxs(DialogActions, {
      "data-element": "DialogActions",
      "data-source-file": "delete-account.tsx",
      children: [/*#__PURE__*/_jsx(Button, {
        variant: "outlined",
        onClick: handleClose,
        "data-element": "Button",
        "data-source-file": "delete-account.tsx",
        children: t('Keep Account')
      }), /*#__PURE__*/_jsx(AsyncButton, {
        status: mutationValue,
        text: [t('Deleting Account...'), t('Delete Account'), t('Delete Account'), t('Delete Account')],
        variant: "contained",
        disabled: !deleteAccountEnabled,
        onClick: function onClick() {
          return deleteAccount();
        },
        "data-element": "AsyncButton",
        "data-source-file": "delete-account.tsx"
      })]
    })]
  });
};

var DeleteAccountSection = function DeleteAccountSection() {
  var _useToggle3 = useToggle(),
      _useToggle4 = _slicedToArray(_useToggle3, 2),
      open = _useToggle4[0],
      _useToggle4$ = _useToggle4[1],
      setOpen = _useToggle4$.on,
      setClosed = _useToggle4$.off;

  var _useTranslation2 = useTranslation(),
      t = _useTranslation2.t;

  return /*#__PURE__*/_jsxs(Box, {
    sx: {
      my: 4,
      display: 'flex'
    },
    "data-element": "Box",
    "data-component": "DeleteAccountSection",
    "data-source-file": "delete-account.tsx",
    children: [/*#__PURE__*/_jsxs(Box, {
      sx: {
        width: '70%'
      },
      "data-element": "Box",
      "data-component": "DeleteAccountSection",
      "data-source-file": "delete-account.tsx",
      children: [/*#__PURE__*/_jsx(Typography, {
        variant: "h3",
        "data-element": "Typography",
        "data-component": "DeleteAccountSection",
        "data-source-file": "delete-account.tsx",
        children: t('Delete account')
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "caption",
        "data-element": "Typography",
        "data-source-file": "delete-account.tsx",
        children: t('Delete all of your login information, data, and points')
      })]
    }), /*#__PURE__*/_jsx(Box, {
      sx: {
        width: '30%',
        height: '50%',
        display: 'flex',
        justifyContent: 'flex-end'
      },
      "data-element": "Box",
      "data-source-file": "delete-account.tsx",
      children: /*#__PURE__*/_jsx(Button, {
        sx: {
          mt: 1
        },
        variant: "outlined",
        color: "error",
        onClick: setOpen,
        "data-element": "Button",
        "data-source-file": "delete-account.tsx",
        children: t('Delete Account')
      })
    }), /*#__PURE__*/_jsx(DeleteAccountModal, {
      open: open,
      setClosed: setClosed,
      "data-element": "DeleteAccountModal",
      "data-source-file": "delete-account.tsx"
    })]
  });
};

export default DeleteAccountSection;